// src/pages/HotWireFoamCutting.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useMemo } from 'react';
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import TutorialWindow from "../components/TutorialWindow";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import { hotWireFoamCuttingTutorial } from "../utils/tutorialContents";
import { MeshProjection } from "../utils/geometryProcessing/MeshProjection";
import { Extrude } from "../utils/geometryProcessing/Extrude";
import { useSyncedCamera } from '../hooks/useSyncedCamera';
import { normalizeModel } from "../utils/normalizeModel";
import "../styles/common.css";

const HotWireFoamCutting = ({ stlFile }) => {
  const containerRef = useRef(null);
  const tutorialRef = useRef(null);

  // Three.js objects
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);

  // Projection models references
  const xyProjectionRef = useRef(null);
  const yzProjectionRef = useRef(null);
  const xzProjectionRef = useRef(null);

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [svgData, setSvgData] = useState({ xy: null, yz: null, xz: null });
  const [showBedSizeSettings, setShowBedSizeSettings] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const pageId = useMemo(() => `hot-wire-foam`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);
const fastIterRecommended = useMemo(() => true, []);
  // Maximum cutting bed dimensions
  const [maxSize, setMaxSize] = useState({
    width: 508, // 20 inch in mm
    height: 812.8, // 32 inch in mm
    depth: 100, // Reasonable cutting depth
  });

  // Helper hooks
  const { createVisualization, removeVisualHelpers, createTextLabel } =
    useVisualHelpers();

  // Materials
  const materials = React.useMemo(
    () => ({
      foamMaterial: new THREE.MeshStandardMaterial({
        color: 0xdce3e7, // 淡蓝灰色
        roughness: 0.9, // 非常粗糙
        metalness: 0.0, // 没有金属感
        transparent: true,
        opacity: 0.95, // 有轻微通透感
        side: THREE.DoubleSide,
      }),
    }),
    []
  );

  // Initialize Three.js scene
  const initScene = useCallback(() => {
    if (!containerRef.current) return;

    // Clear any existing elements
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }

    // Initialize the scene using helper function
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    // Window resize event handler
    const handleResize = () => {
      if (cameraRef.current && rendererRef.current && containerRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(width, height);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (cleanup) cleanup();
    };
  }, []);

  /*   // Create extruded shape from points
  const createExtrudedShape = useCallback((points, depth = 3) => {
    if (!points || points.length < 3) return null;
    
    // Create 2D shape
    const shape = new THREE.Shape();
    points.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point[0], point[1]);
      } else {
        shape.lineTo(point[0], point[1]);
      }
    });
    shape.closePath();

    // Extrude settings
    const extrudeSettings = {
      depth: depth,
      bevelEnabled: false,
    };

    // Create geometry
    const extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
    // Center in Z axis
    extrudeGeometry.computeBoundingBox();
    const boundingBox = extrudeGeometry.boundingBox;
    const offsetZ = (boundingBox.max.z - boundingBox.min.z) / 2;
    extrudeGeometry.translate(0, 0, offsetZ);

    // Create material with wood texture
    const extrudeMaterial = new THREE.MeshStandardMaterial({
      color: 0xd2a679,
      metalness: 0.1,
      roughness: 0.6,
    });

    // Create and return mesh
    const extrudeMesh = new THREE.Mesh(extrudeGeometry, extrudeMaterial);
    return extrudeMesh;
  }, []); */

  // 创建视觉引导
  const createVisualGuides = useCallback(() => {
    if (!objRef.current || !sceneRef.current) return;

    // 清除之前的视觉引导
    const existingGuides = sceneRef.current.children.filter(
      (child) => child.userData && child.userData.isVisualGuide
    );
    existingGuides.forEach((guide) => {
      sceneRef.current.remove(guide);
      if (guide.geometry) guide.geometry.dispose();
      if (guide.material) {
        if (Array.isArray(guide.material)) {
          guide.material.forEach((m) => m.dispose());
        } else {
          guide.material.dispose();
        }
      }
    });

    const modelBoundingBox = new THREE.Box3().setFromObject(objRef.current);
    const modelSize = modelBoundingBox.getSize(new THREE.Vector3());
    const modelCenter = new THREE.Vector3();
    modelBoundingBox.getCenter(modelCenter);

    // 创建指示平面 - 半透明的彩色平面，指示切割方向
    const createIndicatorPlane = (planeType, color, offset) => {
      let planeGeometry, position, rotation;

      if (planeType === "xy") {
        // 顶视图平面 (XY)
        planeGeometry = new THREE.PlaneGeometry(
          modelSize.x * 1.2,
          modelSize.z * 1.2
        );
        position = new THREE.Vector3(
          modelCenter.x,
          modelCenter.y + modelSize.y / 2 + offset,
          modelCenter.z
        );
        rotation = new THREE.Euler(Math.PI / 2, 0, 0);
      } else if (planeType === "yz") {
        // 侧视图平面 (YZ)
        planeGeometry = new THREE.PlaneGeometry(
          modelSize.z * 1.2,
          modelSize.y * 1.2
        );
        position = new THREE.Vector3(
          modelCenter.x - modelSize.x / 2 - offset,
          modelCenter.y,
          modelCenter.z
        );
        rotation = new THREE.Euler(0, Math.PI / 2, 0);
      } else if (planeType === "xz") {
        // 正视图平面 (XZ)
        planeGeometry = new THREE.PlaneGeometry(
          modelSize.x * 1.2,
          modelSize.y * 1.2
        );
        position = new THREE.Vector3(
          modelCenter.x,
          modelCenter.y,
          modelCenter.z + modelSize.z / 2 + offset
        );
        rotation = new THREE.Euler(0, 0, 0);
      }

      const planeMaterial = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.2,
        side: THREE.DoubleSide,
      });

      const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
      planeMesh.position.copy(position);
      planeMesh.rotation.copy(rotation);
      planeMesh.userData.isVisualGuide = true;

      // 添加标签
      let planeLabel = "";
      if (planeType === "xy") planeLabel = "Top View (XY)";
      else if (planeType === "yz") planeLabel = "Side View (YZ)";
      else if (planeType === "xz") planeLabel = "Front View (XZ)";

      const label = createTextLabel(planeLabel, new THREE.Vector3(0, 0, 0), {
        fontSize: 16,
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        textColor: "#000000",
        padding: 8,
        scaleFactor: 0.7,
      });

      planeMesh.add(label);
      return planeMesh;
    };

    // 创建热线切割路径指示 - 彩色线条代表切割路径
    const createCuttingPath = (planeType, color) => {
      let points = [];

      if (planeType === "xy") {
        // 顶部切割路径
        points = [
          new THREE.Vector3(
            modelCenter.x - modelSize.x / 4,
            modelCenter.y + modelSize.y / 2 + 5,
            modelCenter.z
          ),
          new THREE.Vector3(
            modelCenter.x - modelSize.x / 4,
            modelCenter.y - modelSize.y / 2 - 5,
            modelCenter.z
          ),
        ];
      } else if (planeType === "yz") {
        // 侧面切割路径
        points = [
          new THREE.Vector3(
            modelCenter.x - modelSize.x / 2 - 5,
            modelCenter.y + modelSize.y / 4,
            modelCenter.z
          ),
          new THREE.Vector3(
            modelCenter.x + modelSize.x / 2 + 5,
            modelCenter.y + modelSize.y / 4,
            modelCenter.z
          ),
        ];
      } else if (planeType === "xz") {
        // 前面切割路径
        points = [
          new THREE.Vector3(
            modelCenter.x,
            modelCenter.y + modelSize.y / 4,
            modelCenter.z - modelSize.z / 2 - 5
          ),
          new THREE.Vector3(
            modelCenter.x,
            modelCenter.y + modelSize.y / 4,
            modelCenter.z + modelSize.z / 2 + 5
          ),
        ];
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: color,
        linewidth: 3,
        linecap: "round",
      });

      const line = new THREE.Line(geometry, material);
      line.userData.isVisualGuide = true;

      return line;
    };

    // 创建并添加视觉指示平面
    const xyPlane = createIndicatorPlane("xy", 0xff0000, 15);
    const yzPlane = createIndicatorPlane("yz", 0x00aa00, 15);
    const xzPlane = createIndicatorPlane("xz", 0x0055ff, 15);

    sceneRef.current.add(xyPlane);
    sceneRef.current.add(yzPlane);
    sceneRef.current.add(xzPlane);

    // 创建并添加切割路径
    const xyLine = createCuttingPath("xy", 0xff0000);
    const yzLine = createCuttingPath("yz", 0x00aa00);
    const xzLine = createCuttingPath("xz", 0x0055ff);

    sceneRef.current.add(xyLine);
    sceneRef.current.add(yzLine);
    sceneRef.current.add(xzLine);

    // 创建文本说明
    const descriptions = [
      {
        text: "1. Cut with Top Template (XY)",
        position: new THREE.Vector3(
          modelCenter.x + modelSize.x / 2 + 30,
          modelCenter.y + modelSize.y / 2,
          modelCenter.z
        ),
        color: 0xff0000,
      },
      {
        text: "2. Cut with Side Template (YZ)",
        position: new THREE.Vector3(
          modelCenter.x - modelSize.x / 2,
          modelCenter.y + modelSize.y / 2 + 30,
          modelCenter.z
        ),
        color: 0x00aa00,
      },
      {
        text: "3. Cut with Front Template (XZ)",
        position: new THREE.Vector3(
          modelCenter.x,
          modelCenter.y + modelSize.y / 2 + 30,
          modelCenter.z + modelSize.z / 2
        ),
        color: 0x0055ff,
      },
    ];

    descriptions.forEach((desc) => {
      const label = createTextLabel(desc.text, desc.position, {
        fontSize: 16,
        backgroundColor: `rgba(${(desc.color >> 16) & 255}, ${
          (desc.color >> 8) & 255
        }, ${desc.color & 255}, 0.2)`,
        textColor: `rgb(${(desc.color >> 16) & 255}, ${
          (desc.color >> 8) & 255
        }, ${desc.color & 255})`,
        padding: 8,
        scaleFactor: 0.7,
      });
      label.userData.isVisualGuide = true;
      sceneRef.current.add(label);
    });
  }, [objRef, sceneRef, createTextLabel]);

  // Process the model and create projections
  // Process the model and create projections
  const processModel = useCallback(
    (geometry) => {
      if (!geometry || !objRef.current) return;

      // 1. 获取模型的BoundingBox
      const bbox = new THREE.Box3().setFromObject(objRef.current);
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      const size = bbox.getSize(new THREE.Vector3());

      // 2. 定义三个基准面的位置（基于BoundingBox）
      // 计算每个模板之间的间距（基础为模型宽度 + 50mm 空隙）
      const gap = size.x + 50;

      const planeSettings = {
        xy: {
          position: new THREE.Vector3(0, 0, 0),
          rotation: new THREE.Euler(-Math.PI / 2, 0, 0),
          color: 0x0055ff,
        },
        yz: {
          position: new THREE.Vector3(gap, 0, 0),
          rotation: new THREE.Euler(-Math.PI / 2, 0, 0),
          color: 0x00aa00,
        },
        xz: {
          position: new THREE.Vector3(gap * 2, 0, 0),
          rotation: new THREE.Euler(-Math.PI / 2, 0, 0),
          color: 0xff0000,
        },
      };

      // 3. 生成投影
      const projections = MeshProjection.generateProjections(geometry);
      const { projectionResults, svgData: newSvgData } =
        MeshProjection.processProjections(projections, createTextLabel);

      // 4. 创建木色挤出板
      Object.entries(projectionResults).forEach(([plane, result]) => {
        if (result?.line) {
          const settings = planeSettings[plane];

          // 设置投影线
          result.line.position.copy(settings.position);
          result.line.rotation.copy(settings.rotation);
          result.line.material.color.setHex(settings.color);

          // 创建挤出板（关键：使用BoundingBox计算的位置）
          const extrudeMesh = Extrude.createExtrudedShape(
            MeshProjection.extractPointsFromLine(result.line.geometry),
            -3, // 厚度
            {
              position: settings.position,
              rotation: settings.rotation,
              material: new THREE.MeshStandardMaterial({
                color: settings.color,
                metalness: 0.1,
                roughness: 0.6,
              }),
            }
          );
          sceneRef.current.add(extrudeMesh);
        }
      });

      setSvgData(newSvgData);
    },
    [sceneRef, createTextLabel, objRef]
  );

  // Export SVG files
  const exportSVG = useCallback(() => {
    // 使用MeshProjection模块导出SVG文件
    MeshProjection.exportSVG(svgData);

    // 显示教程
    setTutorialVisible(true);
  }, [svgData]);

  // Load STL model
  const loadModel = useCallback(
    (filePath) => {
      setIsLoading(true);
      setError(null);
          //console.log("Loading model: ", filePath);

      // Remove any existing model
      if (objRef.current) {
        sceneRef.current?.remove(objRef.current);
        objRef.current.geometry?.dispose();
        if (Array.isArray(objRef.current.material)) {
          objRef.current.material.forEach((m) => m.dispose());
        } else {
          objRef.current.material?.dispose();
        }
        objRef.current = null;
      }

      // Remove any previous visual helpers
      removeVisualHelpers(sceneRef.current);

      const loader = new STLLoader();
      const path = filePath || "/models/default.stl";

      loader.load(
        path,
        (geometry) => {
              //console.log("Model loaded successfully");

          try {
            // Compute bounding box before transformations
            geometry.computeBoundingBox();
            const originalBoundingBox = geometry.boundingBox.clone();

            // Center the geometry
            geometry.translate(
              -originalBoundingBox.getCenter(new THREE.Vector3()).x,
              -originalBoundingBox.getCenter(new THREE.Vector3()).y,
              -originalBoundingBox.getCenter(new THREE.Vector3()).z
            );
            // Create mesh
            const mesh = new THREE.Mesh(geometry, materials.foamMaterial);
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            // Compute and update model dimensions
            const boundingBox = new THREE.Box3().setFromObject(mesh);
            const size = boundingBox.getSize(new THREE.Vector3());

            // Check if model fits cutting bed in any orientation
            const fitsXY = size.x <= maxSize.width && size.y <= maxSize.height;
            const fitsYZ = size.y <= maxSize.width && size.z <= maxSize.height;
            const fitsXZ = size.x <= maxSize.width && size.z <= maxSize.height;

            if (!fitsXY && !fitsYZ && !fitsXZ) {
              setError(
                `Model cannot fit in any orientation. Max size: ${maxSize.width}x${maxSize.height}mm`
              );
              setIsLoading(false);
              return;
            }

            normalizeModel(mesh);

            // Add to scene
            objRef.current = mesh;
            sceneRef.current.add(mesh);

            // Store dimensions
            setModelDimensions({
              width: size.x,
              height: size.y,
              depth: size.z,
            });

            // Process model to create projections
            processModel(geometry);

            // Create visualization helpers
            const updatedBoundingBox = new THREE.Box3().setFromObject(mesh);
            createVisualization(mesh, sceneRef.current, updatedBoundingBox);
            createVisualGuides();
            setIsLoading(false);
          } catch (error) {
                //console.error("Error processing model:", error);
            setError("Failed to process 3D model");
            setIsLoading(false);
          }
        },
        // Progress callback
        (progress) => {
          const percent = Math.round((progress.loaded / progress.total) * 100);
              //console.log(`Loading progress: ${percent}%`);
        },
        // Error callback
        (error) => {
              //console.error("Error loading STL:", error);
          setError("Failed to load 3D model");
          setIsLoading(false);
        }
      );
    },
    [
      sceneRef,
      removeVisualHelpers,
      materials.foamMaterial,
      processModel,
      createVisualization,
      createVisualGuides,
      maxSize.width,
      maxSize.height,
    ]
  );

  // Initialize the scene when component mounts
  useEffect(() => {
    const cleanup = initScene();
    return () => {
      if (cleanup) cleanup();
    };
  }, [initScene]);

  // Load model when stlFile changes
  useEffect(() => {
    if (stlFile && sceneRef.current) {
      loadModel(stlFile);
    }
  }, [stlFile, loadModel]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container" />
      <button onClick={() => setExpanded((prev) => !prev)} className="panel-toggle-button">
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {/* Control Panel */}
      {!isLoading && expanded &&(
        <div className={`controls-panel ${panelCollapsed ? "collapsed" : ""}`}>
<div className="panel-title-row">
  <div
    className="panel-title"
    onClick={() => setPanelCollapsed(!panelCollapsed)}
  >
    Hot Wire Foam Cutting
    <span className="collapse-icon">{panelCollapsed ? "▼" : "▲"}</span>
  </div>

  {fastIterRecommended && (
    <span
      className="iteration-badge badge--ok"
      title="Simple setup + fast cutting = great for quick iteration"
    >
      Rapid prototyping friendly
    </span>
  )}
</div>


          {!panelCollapsed && (
            <>
              {/* Workflow Info */}
              <div className="panel-section">
                <div className="section-header">
                  <h4>Workflow Information</h4>
                </div>
                <div className="section-content">
                  <p>
                    This workflow is tailored for hot wire foam cutting, which
                    is often used to shape lightweight models such as
                    architectural forms, topography, or visual prototypes. By
                    projecting your 3D model onto three orthogonal planes (XY,
                    YZ, XZ), we can generate flat templates that act as guides
                    for wire movement.
                  </p>
                  <div className="info-item">
                    <strong>How it works:</strong>
                    <ul>
                      <li>XY Projection (Top view)</li>
                      <li>YZ Projection (Side view)</li>
                      <li>XZ Projection (Front view)</li>
                    </ul>
                  </div>
                  <div className="cutting-steps">
                    <strong>Cutting Process:</strong>
                    <div className="step-item">
                      <div
                        className="step-number"
                        style={{ backgroundColor: "#ff0000" }}
                      >
                        1
                      </div>
                      <div className="step-desc">
                        Cut with Top (XY) template
                      </div>
                    </div>
                    <div className="step-item">
                      <div
                        className="step-number"
                        style={{ backgroundColor: "#00aa00" }}
                      >
                        2
                      </div>
                      <div className="step-desc">
                        Cut with Side (YZ) template
                      </div>
                    </div>
                    <div className="step-item">
                      <div
                        className="step-number"
                        style={{ backgroundColor: "#0055ff" }}
                      >
                        3
                      </div>
                      <div className="step-desc">
                        Cut with Front (XZ) template
                      </div>
                    </div>
                    <div className="step-note">
                      Note: Keep foam sections in place until all cuts are
                      complete
                    </div>
                  </div>
                </div>
              </div>

<div className="panel-section">
  <div className="section-header"><h4>Time & Effort</h4></div>
  <div className="section-content">
    <ul style={{ marginLeft: 16, lineHeight: 1.5, fontSize: 13 }}>
      <li><strong>Template prep</strong>: export SVGs and laser-cut cardboard/ply/acrylic.</li>
      <li><strong>Setup</strong>: pin or clamp templates to foam; quick alignment.</li>
      <li><strong>Cutting</strong>: 1–3 passes per side depending on thickness and wire temperature.</li>
      <li><strong>Finishing</strong>: light sanding; optional sealing or coating.</li>
    </ul>
    <p style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
      Tip: do a small test piece to dial in feed rate and wire temperature before the full cut.
    </p>
  </div>
</div>







              {/* Buttons */}
              <div className="buttons-container">
                <button onClick={exportSVG} className="action-button">
                  EXPORT SVG TEMPLATES
                </button>
                <button
                  onClick={() => setTutorialVisible(!tutorialVisible)}
                  className="action-button"
                >
                  {tutorialVisible
                    ? "HIDE INTERACTIVE GUIDANCE"
                    : "SHOW INTERACTIVE GUIDANCE"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Loading & Error */}
      {isLoading && <div className="loading-box">Loading model...</div>}
      {error && <div className="error-box">Error: {error}</div>}

      {/* Tutorial Window */}
      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible(!tutorialVisible)}
        tutorialContent={hotWireFoamCuttingTutorial}
      />
    </div>
  );
};

export default HotWireFoamCutting;
